/**
 * Turning bytes into a file. The DOM half of `contracts/adapter.md` §10, and
 * only the DOM half.
 *
 * Naming is CORE's — `src/core/adapter/filenames.ts` already implements the
 * extension map, the slugifier and the archive name, including the rule that
 * matters most: **no prefix, no suffix, no watermark, ever.** That is a stated
 * product position rather than an oversight to be helpfully corrected, and it
 * should exist once. `WritePayload.filename` and `BatchPayload.archiveName`
 * arrive already named, so the common path does not call any of this; the
 * re-exports are here for the cases that name a file before a write exists.
 *
 * THE ONE RULE that is genuinely this file's: the download must run inside the
 * user-gesture task. Deferring it past an `await` is what makes downloads
 * silently fail in Safari, so `saveBytes` is synchronous from its first line to
 * its last and the bytes have to be in hand before the click handler runs.
 */

import type { WriteOptions } from '../../../../../contracts/cue-model.ts';
import {
  archiveName,
  basenameOf,
  EXTENSIONS,
  outputName,
  slugifyBasename,
} from '../../../../../src/core/adapter/filenames.ts';

export { archiveName, basenameOf, EXTENSIONS, outputName, slugifyBasename };

/**
 * `cd1.srt` → `cd1.part1.srt`, for the two halves of a split.
 *
 * The one suffix this site adds, and only because a split hands back two files
 * from one source: CORE names both `cd1.srt`, and two downloads with one name
 * become `cd1 (1).srt` in a downloads folder, which says nothing about which
 * half is which. The number goes before the extension so each part still opens
 * as what it is and the two sort together.
 */
export function partName(filename: string, part: number): string {
  const dot = filename.lastIndexOf('.');
  return dot > 0
    ? `${filename.slice(0, dot)}.part${part}${filename.slice(dot)}`
    : `${filename}.part${part}`;
}

/**
 * Content types for the formats where the browser genuinely needs one. Text
 * subtitle formats are all `text/plain`; getting clever here is how a `.srt`
 * ends up opening in a preview tab instead of downloading.
 */
const MIME: Readonly<Record<string, string>> = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  json: 'application/json',
  zip: 'application/zip',
  // Contract 1.13.0. TTML has its own type (TTML1 registers application/ttml+xml), and an
  // EBU STL file is binary. SCC is plain ASCII text and keeps the default.
  ttml: 'application/ttml+xml',
  'ebu-stl': 'application/octet-stream',
};

/**
 * The Blob's type: the format's own where it has one, otherwise plain text
 * labelled with the charset the bytes are actually in. An SMI or SRT saved as
 * EUC-KR (contracts 1.7.0 and 1.8.0) arrives as EUC-KR bytes, and labelling
 * them UTF-8 would be a false statement to anything that reads the label.
 */
export function blobType(format: string, encoding: WriteOptions['encoding'] = 'utf-8'): string {
  return MIME[format] ?? `text/plain;charset=${encoding}`;
}

/**
 * Save bytes as a file. Synchronous by design — see the note at the top.
 *
 * The bytes come from the library and cross the worker boundary by transfer,
 * so the Blob is built once here from a buffer nobody else holds.
 *
 * `encoding` is the one the write was asked for. A string is always UTF-8;
 * EUC-KR always arrives as bytes.
 */
export function saveBytes(
  bytes: Uint8Array | string,
  filename: string,
  format: string,
  encoding: WriteOptions['encoding'] = 'utf-8',
): void {
  const type = blobType(format, encoding);
  const blob =
    typeof bytes === 'string'
      ? new Blob([bytes], { type })
      : new Blob([bytes as BlobPart], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  anchor.style.display = 'none';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // Next task, not next microtask: revoking too early cancels the download in
  // some engines.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
